import {
  ConfiguredDocument,
  DocumentsConfig,
  SelectedThirdParty,
} from 'pages/initiate-due-diligence/types';
import { hasValidVerificationDateConfig, isVerificationDateValid } from './verificationDateHelper';
import { ThirdPartyType } from 'types';
import { CreateErrorOptions, ValidationError } from 'yup';
import dayjs from 'dayjs';
export interface Document {
  id: string;
  name: string;
  category: string;
  categoryId: number;
  size: number;
  type: string;
  date: string;
  verificationDate: string | null;
  isOptionalDocument?: boolean;
}

/**
 * Document type from Backend
 */
export interface FileInformationResponse {
  id: string;
  name: string;
  fileDownloadName: string;
  category: string;
  categoryId: number;
  size: number;
  type: string;
  thirdPartyId: string;
  verificationDate: string;
  uploadedBy: string;
  uploadedDate: string;
  documentSensitivity: boolean;
  expiryDate: string | null;
  fileStatus?: string | null;
  extension: string;
  isCbiFile: boolean;
}

/**
 * Document type for Cards
 */
export interface FileInfo {
  id: string;
  name: string;
  category: string;
  categoryId: number;
  type: string;
  uploadedDate: string;
  verificationDate: string | null;
  size: number;
  uploadedBy: string;
  fileDownloadName: string;
  extension: string;
  documentSensitivity: boolean;
  expiryDate: string | null;
  isChecked?: boolean;
  isCbiFile: boolean;
  thirdPartyId: string;
  fileStatus?: string | null;
}

/**
 * Document type in DocumentsData form field
 */
export interface DocumentData {
  name: string;
  size: number;
  type: string;
  category: string;
  categoryId: number;
  entityId?: string;
  mainThirdPartyId?: string;
  verificationDate: string | null;
  id: string;
  fileDownloadName: string;
  uploadedBy: string;
  uploadedDate: string;
  documentSensitivity: boolean;
  expiryDate: string | null;
  isOptionalDocument: boolean;
  isLinkedThirdPartyDocument: boolean;
  fileStatus?: string | null;
  errorMessage?: string | null;
}

export enum FileFormat {
  PNG = 'png',
  JPEG = 'jpeg',
  JPG = 'jpg',
  CSV = 'csv',
  PDF = 'pdf',
  DOCX = 'docx',
  XLSX = 'xlsx',
  ZIP = 'zip',
  MP4 = 'mp4',
  VTT = 'vtt',
}

export const fileInformationResponseToDocumentData = (
  fileInformationResponse: FileInformationResponse,
  isRelatedEntityDocument: boolean | undefined = undefined,
  entityId: string | undefined = undefined,
  isOptional: boolean | undefined = undefined
) => {
  const documentData: DocumentData = {
    name: fileInformationResponse.name,
    size: fileInformationResponse.size,
    type: getFileExtension(fileInformationResponse.fileDownloadName),
    category: fileInformationResponse.category,
    categoryId: fileInformationResponse.categoryId,
    entityId: entityId,
    mainThirdPartyId: fileInformationResponse.thirdPartyId,
    verificationDate: fileInformationResponse.verificationDate,
    documentSensitivity: fileInformationResponse.documentSensitivity,
    expiryDate: fileInformationResponse.expiryDate,
    fileStatus: fileInformationResponse.fileStatus,

    id: fileInformationResponse.id,
    fileDownloadName: fileInformationResponse.fileDownloadName,
    uploadedBy: fileInformationResponse.uploadedBy,
    uploadedDate: fileInformationResponse.uploadedDate,
    isOptionalDocument: isOptional ?? false,
    isLinkedThirdPartyDocument: isRelatedEntityDocument ?? false,
  };

  return documentData;
};

export enum AllowedFiles {
  DocumentMaxByteSize = 20 * 1024 * 1024,
}

export const calculateFileSize = (size: number): string => {
  if (size === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(size) / Math.log(k));
  return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileExtension = (filename: string): string => {
  const regex = /(?:\.([^.]+))?$/;
  const match = regex.exec(filename);
  if (match) {
    return match[1];
  }
  return '';
};

/**
 * delete verificationDateConfig when verificationDate is disabled
 * */
export const normalizeVerificationDateConfigs = (documentsConfigRaw: DocumentsConfig) => {
  const normalizeVerificationDateConfig = (configuredDoc: ConfiguredDocument) => {
    if (!configuredDoc.verificationDateEnabled) {
      return null;
    }

    const verificationDateConfig = configuredDoc.verificationDateConfig;

    if (!verificationDateConfig) {
      throw new Error('verificationDateConfig is required when verificationDateEnabled is true');
    }

    return verificationDateConfig;
  };

  documentsConfigRaw.thirdPartyDocuments.thirdPartyConfiguredDocumentsIndividual =
    documentsConfigRaw.thirdPartyDocuments.thirdPartyConfiguredDocumentsIndividual.map(
      (configuredDoc) => ({
        ...configuredDoc,
        verificationDateConfig: normalizeVerificationDateConfig(configuredDoc),
      })
    );

  documentsConfigRaw.thirdPartyDocuments.thirdPartyConfiguredDocumentsBusiness =
    documentsConfigRaw.thirdPartyDocuments.thirdPartyConfiguredDocumentsBusiness.map(
      (configuredDoc) => ({
        ...configuredDoc,
        verificationDateConfig: normalizeVerificationDateConfig(configuredDoc),
      })
    );

  documentsConfigRaw.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsIndividual =
    documentsConfigRaw.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsIndividual.map(
      (configuredDoc) => ({
        ...configuredDoc,
        verificationDateConfig: normalizeVerificationDateConfig(configuredDoc),
      })
    );

  documentsConfigRaw.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsBusiness =
    documentsConfigRaw.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsBusiness.map(
      (configuredDoc) => ({
        ...configuredDoc,
        verificationDateConfig: normalizeVerificationDateConfig(configuredDoc),
      })
    );

  return documentsConfigRaw;
};

export const isIndividualRelatedEntityDocumentRequiredDependingOnAge = (
  relatedEntity: SelectedThirdParty | undefined,
  configuredDoc: ConfiguredDocument
): boolean => {
  if (!relatedEntity) {
    return false;
  }

  if (!relatedEntity.dateOfBirth) {
    return true;
  }

  const age = dayjs().diff(dayjs(relatedEntity.dateOfBirth), 'year');
  return age >= configuredDoc.ageOptionalDocOverride;
};

export const validateRequiredDocumentData = (
  errorMessage: string,
  documents: DocumentData[],
  documentsConfig: DocumentsConfig,
  thirdPartyType: ThirdPartyType,
  selectedRelatedEntities: SelectedThirdParty[],
  createError: (params?: CreateErrorOptions) => ValidationError,
  validateMandatoryDocuments = true
) => {
  if (!validateMandatoryDocuments) {
    return true;
  }

  if (!documentsConfig) {
    return createError({
      message: errorMessage,
      type: `required-documentsConfig`,
    });
  }

  if (documentsConfig.isRequiredDocumentsForThirdParty) {
    const thirdPartyDocs = documents.filter(
      (doc) => !doc.isLinkedThirdPartyDocument && !doc.isOptionalDocument
    );

    if (thirdPartyType == ThirdPartyType.Business) {
      for (const configuredDoc of documentsConfig.thirdPartyDocuments
        .thirdPartyConfiguredDocumentsBusiness) {
        const foundDoc = thirdPartyDocs.find((doc) => doc.categoryId === configuredDoc.categoryId);

        if (!foundDoc) {
          return createError({
            message: errorMessage,
            type: `required-documentsData-${configuredDoc.categoryId}`,
          });
        }
      }
    } else {
      for (const configuredDoc of documentsConfig.thirdPartyDocuments
        .thirdPartyConfiguredDocumentsIndividual) {
        const foundDoc = thirdPartyDocs.find((doc) => doc.categoryId === configuredDoc.categoryId);

        if (!foundDoc) {
          return createError({
            message: errorMessage,
            type: `required-documentsData-${configuredDoc.categoryId}`,
          });
        }
      }
    }
  }

  if (documentsConfig.isRequiredDocumentsForRelatedThirdParties) {
    const relatedEntitiesDocs = documents.filter((doc) => doc.isLinkedThirdPartyDocument);

    for (const relatedEntity of selectedRelatedEntities) {
      if (relatedEntity.type == ThirdPartyType.Business) {
        for (const configuredDoc of documentsConfig.relatedThirdPartiesDocuments
          .linkedThirdPartyConfiguredDocumentsBusiness) {
          const foundDoc = relatedEntitiesDocs.find(
            (doc) => doc.categoryId === configuredDoc.categoryId
          );

          if (!foundDoc) {
            return createError({
              message: errorMessage,
              type: `required-documentsData-${configuredDoc.categoryId}`,
            });
          }
        }
      } else {
        for (const configuredDoc of documentsConfig.relatedThirdPartiesDocuments
          .linkedThirdPartyConfiguredDocumentsIndividual) {
          if (
            !isIndividualRelatedEntityDocumentRequiredDependingOnAge(relatedEntity, configuredDoc)
          ) {
            return true;
          }

          const foundDoc = relatedEntitiesDocs.find(
            (doc) => doc.categoryId === configuredDoc.categoryId
          );

          if (!foundDoc) {
            return createError({
              message: errorMessage,
              type: `required-documentsData-${configuredDoc.categoryId}`,
            });
          }
        }
      }
    }
  }

  return true;
};

export const validateNoDuplicatedDocumentData = (
  errorMessage: string,
  documents: DocumentData[],
  createError: (params?: CreateErrorOptions) => ValidationError
) => {
  const uniqueDocuments = new Set();

  for (const doc of documents) {
    const docName = doc.name;
    if (uniqueDocuments.has(docName)) {
      return createError({
        message: errorMessage,
        type: `uniqueName-documentsData-${doc.categoryId}-${
          doc.isOptionalDocument ? 'optional' : ''
        }`,
      });
    } else {
      uniqueDocuments.add(docName);
    }
  }
  return true;
};

const validateAgainstVerificationDateConfig = (config: ConfiguredDocument, doc: DocumentData) => {
  if (!config?.verificationDateEnabled) {
    return true;
  }

  if (!doc.verificationDate) {
    return false;
  }

  if (!hasValidVerificationDateConfig(config.verificationDateConfig)) {
    return true;
  }

  return isVerificationDateValid(config.verificationDateConfig, doc.verificationDate);
};

export const validateVerificationDateDocumentData = (
  errorMessage: string,
  documents: DocumentData[],
  documentsConfig: DocumentsConfig,
  createError: (params?: CreateErrorOptions) => ValidationError
) => {
  if (!documentsConfig) {
    return createError({
      message: errorMessage,
      type: `required-documentsConfig`,
    });
  }

  const thirdPartyConfiguredDocs = [
    ...documentsConfig.thirdPartyDocuments.thirdPartyConfiguredDocumentsIndividual,
    ...documentsConfig.thirdPartyDocuments.thirdPartyConfiguredDocumentsBusiness,
  ];

  const relatedEntitiesConfiguredDocs = [
    ...documentsConfig.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsIndividual,
    ...documentsConfig.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsBusiness,
  ];

  const thirdPartyDocs = documents.filter(
    (doc) => !doc.isLinkedThirdPartyDocument && !doc.isOptionalDocument
  );

  const relatedEntitiesDocs = documents.filter((doc) => doc.isLinkedThirdPartyDocument);

  for (const doc of thirdPartyDocs) {
    const config = thirdPartyConfiguredDocs.find(({ categoryId }) => categoryId === doc.categoryId);

    if (!validateAgainstVerificationDateConfig(config, doc)) {
      return createError({
        message: errorMessage,
        type: `verificationDate-documentsData-${config.categoryId}
          }`,
      });
    }
  }

  for (const doc of relatedEntitiesDocs) {
    const config = relatedEntitiesConfiguredDocs.find(
      ({ categoryId }) => categoryId === doc.categoryId
    );

    if (!validateAgainstVerificationDateConfig(config, doc)) {
      return createError({
        message: errorMessage,
        type: `verificationDate-documentsData-${config.categoryId}
          }`,
      });
    }
  }

  return true;
};
